import type { ISwSettings } from "app/@types/ISwSettings";
import { isDMIAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

const SHOPPER_ICP_INDUSTRIES = ["CPG", "Agency", "Big Tech", "Retail / eCommerce"];
const SHOPPER_ICP_GEOGRAPHIES = ["CN", "US", "CA", "GB", "UK", "DE", "AT", "CH", "FR", "BE", "IT", "IN"];
const EXCLUDED_TITLE_KEYWORDS = ["SEO", "Student", "Teacher"];
const GENERIC_EMAIL_DOMAINS = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "aol.com",
    "icloud.com",
    "qq.com",
    "tencent.com",
];

const INDUSTRY_COMPANY_SIZE_REQUIREMENTS: Array<{ keywords: string[]; minSize: number }> = [
    { keywords: ["cpg", "retail", "ecommerce"], minSize: 1000 },
    { keywords: ["agency"], minSize: 100 },
    { keywords: ["big tech"], minSize: 1000 },
];

type DataInput = string | null | undefined;

const parseCompanySize = (employees: DataInput): number | null => {
    const match = employees?.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
};

const isGenericEmailDomain = (email: DataInput): boolean => {
    const domain = email?.split("@")[1]?.toLowerCase();
    if (!domain) return true;

    return GENERIC_EMAIL_DOMAINS.some((genericDomain) => domain.includes(genericDomain));
};

const hasCompanyWebsite = (website: DataInput): boolean => !!website && website.trim().length > 0;

const isTitleExcluded = (title: DataInput): boolean => {
    if (!title) return false;
    const titleLower = title.toLowerCase();
    return EXCLUDED_TITLE_KEYWORDS.some((keyword) => titleLower.includes(keyword.toLowerCase()));
};

const checkIndustry = (industry: DataInput): boolean => {
    if (!industry) return false;
    return SHOPPER_ICP_INDUSTRIES.some((icpIndustry) => industry.toLowerCase().includes(icpIndustry.toLowerCase()));
};

const checkCompanySize = (industry: DataInput, employees: DataInput): boolean => {
    const companySize = parseCompanySize(employees);

    if (companySize === null) return false;

    if (!industry) return false;
    const industryLower = industry.toLowerCase();

    return INDUSTRY_COMPANY_SIZE_REQUIREMENTS.some(
        (requirement) =>
            requirement.keywords.some((keyword) => industryLower.includes(keyword)) &&
            companySize >= requirement.minSize,
    );
};

const checkGeography = (phoneCountryCode: DataInput): boolean =>
    SHOPPER_ICP_GEOGRAPHIES.includes(phoneCountryCode?.toUpperCase() || "");

export const isShopperIcpEligible = (swSettings: ISwSettings): boolean => {
    const { user } = swSettings;
    const quiz = user?.customData?.quiz;

    if (!quiz) return false;

    if (!isDMIAfterTrial(swSettings)) return false;

    const { industry, employees, website, role: title, phoneCountryCode } = quiz;
    const { username: email } = user;

    return (
        checkIndustry(industry) &&
        checkCompanySize(industry, employees) &&
        !isGenericEmailDomain(email) &&
        hasCompanyWebsite(website) &&
        !isTitleExcluded(title) &&
        checkGeography(phoneCountryCode)
    );
};
